import React, { useState, useEffect, useRef } from 'react';
import { Col, Text, Input, TouchField, Row, useUIState, Button } from 'components';
import { COLOR, COLORS } from 'const';
import Store from 'store';
import { ActivityIndicator, ScrollView } from 'react-native';
import { TimeHelper, calculateTimeOpen } from 'helpers';
import { Button as AntButton } from 'antd';
import ImageItem from 'components/elements/image-gallery/ImageItem';

interface ChatMessage {
  id: string;
  issueId: string;
  userId: string;
  adminId?: string | null;
  message: string;
  attachFiles?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface OrderNote {
  id: string;
  content: string;
  createdAt?: string;
  createdBy?: string;
}

interface OrderIssue {
  id: string;
  orderId: string;
  userId: string;
  type: 'missing' | 'wrong-item' | 'damaged' | 'other';
  description: string;
  status: 'pending' | 'in-progress' | 'resolved' | 'rejected';
  attachFiles?: string[];
  createdAt?: string;
  updatedAt?: string;
  order?: { id?: string; sorId?: string; notes?: OrderNote[] };
}

interface ChatMessagesContentProps {
  issueId: string;
  issue?: any;
  onStatusUpdate?: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
}

export const ChatMessagesContent = ({ 
  issueId, 
  issue: initialIssue, 
  onStatusUpdate,
  onClose,
  showCloseButton = false
}: ChatMessagesContentProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [issue, setIssue] = useState<OrderIssue | null>(initialIssue || null);
  const [{ fetching, loading: sending }, setUI] = useUIState({
    fetching: false,
    loading: false,
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pagination, setPagination] = useState({ hasNext: false, total: 0 });
  const scrollViewRef = useRef<ScrollView>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [newNote, setNewNote] = useState('');
  const [noteActionLoading, setNoteActionLoading] = useState(false);

  const getMessages = async (reset = true, shouldScroll = true) => {
    try {
      setUI({ fetching: true });

      const res = await Store.Api.Shopify.listChatMessages({
        issueId,
        limit: 100,
        offset: reset ? 0 : messages.length,
      });

      if (res.data.success) {
        const newMessages = res.data.data.messages || [];
        setMessages(reset ? newMessages : [...messages, ...newMessages]);
        setPagination({
          hasNext: res.data.data.hasNext || false,
          total: res.data.data.total || 0,
        });

        if (shouldScroll) {
          setTimeout(() => {
            scrollViewRef.current?.scrollToEnd({ animated: true });
          }, 100);
        }
      } else {
        setUI({ fetching: false, errorMes: res.data.error || "Failed to load messages" });
        return;
      }

      setUI({ fetching: false });
    } catch (err) {
      setUI({ fetching: false, errorMes: String(err) });
    }
  };

  const getIssue = async (forceFetch = false) => {
    if (initialIssue && !forceFetch) {
      setIssue(initialIssue);
      return;
    }

    try {
      const res = await Store.Api.Shopify.getOrderIssue({
        id: issueId,
      });

      if (res.data.success) {
        setIssue(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch issue:', err);
    }
  };

  useEffect(() => {
    getMessages(true, true);
    getIssue();

    pollingIntervalRef.current = setInterval(() => {
      getMessages(true, false);
    }, 3000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [issueId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setUI({ loading: true });

      const res = await Store.Api.Shopify.createChatMessage({
        issueId,
        message: newMessage.trim(),
      });

      if (res.data.success) {
        setNewMessage('');
        // Reload messages to get the new one
        await getMessages(true);
      } else {
        setUI({ loading: false, errorMes: res.data.error || "Failed to send message" });
      }

      setUI({ loading: false });
    } catch (err) {
      setUI({ loading: false, errorMes: String(err) });
    }
  };

  const handleAddNote = async () => {
    if (!issue?.orderId || !newNote.trim() || !isAdmin) return;
    try {
      setNoteActionLoading(true);
      const res = await Store.Api.Shopify.addOrderNote({
        orderId: issue.orderId,
        noteContent: newNote.trim(),
        action: 'add',
      });
      if (res.data.success) {
        setNewNote('');
        await getIssue(true);
        if (onStatusUpdate) onStatusUpdate();
      } else {
        setUI({ errorMes: res.data.error || 'Failed to add note' });
      }
    } catch (err) {
      setUI({ errorMes: String(err) });
    } finally {
      setNoteActionLoading(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!issue?.orderId || !isAdmin) return;
    try {
      setNoteActionLoading(true);
      const res = await Store.Api.Shopify.addOrderNote({
        orderId: issue.orderId,
        noteId,
        action: 'delete',
      });
      if (res.data.success) {
        await getIssue(true);
        if (onStatusUpdate) onStatusUpdate();
      } else {
        setUI({ errorMes: res.data.error || 'Failed to remove note' });
      }
    } catch (err) {
      setUI({ errorMes: String(err) });
    } finally {
      setNoteActionLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus: 'resolved' | 'rejected') => {
    if (!issue) return;

    try {
      setUpdatingStatus(true);

      const res = await Store.Api.Shopify.updateOrderIssue({
        id: issue.id,
        status: newStatus,
      });

      if (res.data.success) {
        setIssue(res.data.data);
        if (onStatusUpdate) {
          onStatusUpdate();
        }
      } else {
        setUpdatingStatus(false);
        setUI({ errorMes: res.data.error || "Failed to update issue status" });
        return;
      }

      setUpdatingStatus(false);
    } catch (err) {
      setUpdatingStatus(false);
      setUI({ errorMes: String(err) });
    }
  };

  const UserStore = Store.useUserStore();
  const { user } = UserStore;
  const isAdmin = user?.role === 'admin';
  const isCreator = issue?.userId === user?.id;
  const canUpdateStatus = isAdmin || isCreator;
  const statusColors: { [key: string]: string } = {
    pending: '#faad14',
    'in-progress': '#1890ff',
    resolved: '#52c41a',
    rejected: '#ff4d4f',
  };

  return (
    <Col flex1 style={{ overflow: 'hidden' }}>
      {/* Header */}
      <Row justifyContent="space-between" alignItems="center" p2 style={{ borderBottomWidth: 1, borderBottomColor: COLOR.GREY_BG }}>
        <Col>
          <Text fontSize={18} bold>Order Issue Chat</Text>
          {issue?.createdAt && (() => {
            const timeOpen = calculateTimeOpen(issue.createdAt, issue.updatedAt, issue.status);
            const colorMap = {
              green: '#52c41a',
              amber: '#faad14',
              red: '#ff4d4f',
            };
            return (
              <Row alignItems="center" mt1 gap={4}>
                <Text fontSize={12} color={COLOR.GREY}>Open for:</Text>
                <Text fontSize={12} style={{ 
                  color: colorMap[timeOpen.color],
                  fontWeight: timeOpen.isOverSLA ? 'bold' : 'normal',
                }}>
                  {timeOpen.formatted}
                  {timeOpen.isOverSLA && ' (Exceeded SLA)'}
                </Text>
              </Row>
            );
          })()}
        </Col>
        {showCloseButton && onClose && (
          <TouchField
            width={30}
            height={30}
            middle
            borderRadius={15}
            backgroundColor={COLOR.GREY_BG}
            onPress={onClose}
          >
            <Text fontSize={20} color={COLOR.GREY}>×</Text>
          </TouchField>
        )}
      </Row>

      {/* Main Content */}
      <Row flex1 style={{ overflow: 'hidden' }}>
        {/* Order Issue Details - Left Side */}
        {issue && (
          <Col width={380} style={{ borderRightWidth: 1, borderRightColor: COLOR.GREY_BG, overflow: 'hidden' }}>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
              <Col p2 backgroundColor={COLOR.GREY_BG} round1>
                <Row justifyContent="space-between" alignItems="center" mb2>
                  <Text bold fontSize={16}>Order Issue Details</Text>
                  <Text
                    fontSize={11}
                    style={{
                      color: statusColors[issue.status] || '#000',
                      fontWeight: 'bold',
                      textTransform: 'capitalize',
                      paddingHorizontal: 10,
                      paddingVertical: 5,
                      backgroundColor: 'white',
                      borderRadius: 12,
                    }}
                  >
                    {issue.status?.replace('-', ' ')}
                  </Text>
                </Row>

                {issue.createdAt && (() => {
                  const timeOpen = calculateTimeOpen(issue.createdAt, issue.updatedAt, issue.status);
                  const colorMap = {
                    green: '#52c41a',
                    amber: '#faad14',
                    red: '#ff4d4f',
                  };
                  return (
                    <Col mb2 p1 backgroundColor="white" round1 style={{ borderWidth: 1, borderColor: colorMap[timeOpen.color] + '40' }}>
                      <Row alignItems="center" justifyContent="space-between">
                        <Row alignItems="center" gap={6}>
                          <Text fontSize={11} color={COLOR.GREY}>Time Open:</Text>
                          <Text fontSize={13} style={{ 
                            color: colorMap[timeOpen.color],
                            fontWeight: timeOpen.isOverSLA ? 'bold' : '600',
                          }}>
                            {timeOpen.formatted}
                          </Text>
                        </Row>
                        {timeOpen.isOverSLA && (
                          <Text fontSize={10} style={{ color: colorMap[timeOpen.color], fontWeight: 'bold' }}>
                            ⚠️ Exceeded SLA
                          </Text>
                        )}
                      </Row>
                      <Text fontSize={10} color={COLOR.GREY} mt1>
                        {timeOpen.isOverSLA 
                          ? 'This issue has exceeded the 48h SLA threshold'
                          : timeOpen.color === 'amber'
                            ? 'This issue is approaching the 48h SLA threshold'
                            : 'This issue is within SLA'}
                      </Text>
                    </Col>
                  );
                })()}

                <Col mb2>
                  <Text caption color={COLOR.GREY} mb1>Issue ID:</Text>
                  <Text fontSize={12} style={{ fontFamily: 'monospace' }}>{issue.id}</Text>
                </Col>

                <Col mb2>
                  <Text caption color={COLOR.GREY} mb1>Order ID:</Text>
                  <Text fontSize={12} style={{ fontFamily: 'monospace' }}>{issue.orderId}</Text>
                </Col>

                {issue.order?.sorId ? (
                  <Col mb2>
                    <Text caption color={COLOR.GREY} mb1>BC Order ID:</Text>
                    <Text fontSize={12} style={{ fontFamily: 'monospace' }}>{issue.order.sorId}</Text>
                  </Col>
                ) : null}

                <Row mb2>
                  <Col flex1>
                    <Text caption color={COLOR.GREY} mb1>Type:</Text>
                    <Text fontSize={12} style={{ textTransform: 'capitalize' }}>
                      {issue.type?.replace('-', ' ')}
                    </Text>
                  </Col>
                  <Col flex1>
                    <Text caption color={COLOR.GREY} mb1>Created:</Text>
                    <Text fontSize={12}>
                      {issue.createdAt ? TimeHelper.formatDay(issue.createdAt) : '-'}
                    </Text>
                  </Col>
                </Row>

                <Col mb2>
                  <Text caption color={COLOR.GREY} mb1>Description:</Text>
                  <Text fontSize={12} style={{ lineHeight: 18 }}>{issue.description}</Text>
                </Col>

                {issue.attachFiles && issue.attachFiles.length > 0 && (
                  <Col mb2>
                    <Text caption color={COLOR.GREY} mb1>Attached Files:</Text>
                    <Row flexWrap="wrap" gap={8} mt1>
                      {issue.attachFiles.map((fileUrl, index) => (
                        <ImageItem
                          key={index}
                          uri={fileUrl}
                          style={{ width: 100, height: 100 }}
                        />
                      ))}
                    </Row>
                  </Col>
                )}

                {/* Order Notes */}
                <Col mb2 p2 backgroundColor="white" round1 style={{ borderWidth: 1, borderColor: COLOR.GREY_BG }}>
                  <Text bold fontSize={14} mb2>Order Notes</Text>
                  {issue.order?.notes && issue.order.notes.length > 0 ? (
                    <Col mb2>
                      {issue.order.notes.map((note) => (
                        <Col
                          key={note.id}
                          mb2
                          p2
                          round1
                          style={{ backgroundColor: COLOR.GREY_BG, borderLeftWidth: 3, borderLeftColor: COLOR.MAIN }}
                        >
                          <Text fontSize={12} style={{ lineHeight: 18 }}>{note.content}</Text>
                          <Row mt1 justifyContent="space-between" alignItems="center">
                            <Text caption color={COLOR.GREY} fontSize={10}>
                              {note.createdAt ? TimeHelper.formatDay(note.createdAt) : ''}
                            </Text>
                            {isAdmin && (
                              <AntButton
                                type="text"
                                size="small"
                                danger
                                onClick={() => handleDeleteNote(note.id)}
                                disabled={noteActionLoading}
                                style={{ padding: 0, minWidth: 0, height: 'auto' }}
                              >
                                Remove
                              </AntButton>
                            )}
                          </Row>
                        </Col>
                      ))}
                    </Col>
                  ) : (
                    <Text caption color={COLOR.GREY} mb2>No notes yet.</Text>
                  )}
                  {isAdmin && (
                    <Col>
                      <Input
                        placeholder="Add a note..."
                        value={newNote}
                        onChange={setNewNote}
                        multiline
                        height={56}
                        inputProps={{ numberOfLines: 2 }}
                      />
                      <AntButton
                        type="primary"
                        size="small"
                        onClick={handleAddNote}
                        loading={noteActionLoading}
                        disabled={!newNote.trim()}
                        style={{ marginTop: 8, backgroundColor: COLORS.GREEN }}
                      >
                        Add Note
                      </AntButton>
                    </Col>
                  )}
                </Col>

                {canUpdateStatus && issue.status !== 'resolved' && issue.status !== 'rejected' && (
                  <Col mt2>
                    <Row gap={8} flexWrap="wrap">
                      <AntButton
                        size="small"
                        onClick={() => handleUpdateStatus('resolved')}
                        loading={updatingStatus}
                        style={{ backgroundColor: COLORS.GREEN, flex: 1, minWidth: 140 }}
                      >
                        <Text fontSize={13} fontWeight="600" colorWhite>Mark as Resolved</Text>
                      </AntButton>
                      <AntButton
                        size="small"
                        onClick={() => handleUpdateStatus('rejected')}
                        loading={updatingStatus}
                        style={{ backgroundColor: COLORS.RED, flex: 1, minWidth: 140 }}
                      >
                        <Text fontSize={13} fontWeight="600" colorWhite>Mark as Rejected</Text>
                      </AntButton>
                    </Row>
                  </Col>
                )}
              </Col>
            </ScrollView>
          </Col>
        )}

        {/* Chat Messages - Right Side */}
        <Col flex1 style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Chat Header */}
          <Row justifyContent="space-between" alignItems="center" p2 style={{ borderBottomWidth: 1, borderBottomColor: COLOR.GREY_BG }}>
            <Text fontSize={16} bold>Chat Messages</Text>
            {issue && (
              <Text caption color={COLOR.GREY}>
                {pagination.total} message{pagination.total !== 1 ? 's' : ''}
              </Text>
            )}
          </Row>

          {/* Messages Area */}
          <Col flex1 style={{ overflow: 'hidden', minHeight: 300 }}>
            {fetching && messages.length === 0 ? (
              <Col flex1 middle>
                <ActivityIndicator size="large" />
                <Text mt2 color={COLOR.GREY}>Loading messages...</Text>
              </Col>
            ) : messages.length === 0 ? (
              <Col flex1 middle>
                <Text color={COLOR.GREY}>No messages yet. Start the conversation!</Text>
              </Col>
            ) : (
              <ScrollView
                ref={scrollViewRef}
                style={{
                  height: '60vh',
                }}
                contentContainerStyle={{ padding: 16 }}
              >
                {messages.map((msg) => {
                  const isAdmin = !!msg.adminId;
                  const isCurrentUser = msg.userId === user?.id;

                  return (
                    <Col
                      key={msg.id}
                      mb2
                      style={{
                        alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                        maxWidth: '75%',
                      }}
                    >
                      <Row mb1 style={{ alignSelf: isCurrentUser ? 'flex-end' : 'flex-start' }}>
                        <Text
                          caption
                          color={COLOR.GREY}
                          fontSize={11}
                          style={{
                            fontWeight: isAdmin ? 'bold' : 'normal',
                          }}
                        >
                          {isAdmin ? 'Admin' : 'User'}
                          {msg.createdAt && ` • ${TimeHelper.formatDay(msg.createdAt)}`}
                        </Text>
                      </Row>
                      <Col
                        p2
                        round1
                        backgroundColor={isCurrentUser ? COLOR.MAIN : COLOR.GREY_BG}
                        style={{
                          alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
                          flexShrink: 1,
                        }}
                      >
                        {msg.message !== '(Attachment)' && (
                          <Text
                            fontSize={14}
                            color={isCurrentUser ? 'white' : 'black'}
                            style={{ lineHeight: 20 }}
                          >
                            {msg.message}
                          </Text>
                        )}
                        {msg.attachFiles && msg.attachFiles.length > 0 && (
                          <Row flexWrap="wrap" gap={8} mt={msg.message && msg.message !== '(Attachment)' ? 2 : 0}>
                            {msg.attachFiles.map((fileUrl, index) => (
                              <ImageItem
                                key={index}
                                uri={fileUrl}
                                style={{ width: 80, height: 80 }}
                              />
                            ))}
                          </Row>
                        )}
                      </Col>
                    </Col>
                  );
                })}
                {pagination.hasNext && (
                  <Row justifyContent="center" mt2 mb2>
                    <Button
                      text="Load More"
                      onPress={() => getMessages(false)}
                      isLoading={fetching}
                      fitContent
                    />
                  </Row>
                )}
              </ScrollView>
            )}
          </Col>

          {/* Input Area */}
          <Col p2 style={{ borderTopWidth: 1, borderTopColor: COLOR.GREY_BG, backgroundColor: 'white' }}>
            <Row gap={10} alignItems="flex-end">
              <Col flex1>
                <Input
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={setNewMessage}
                  multiline
                  height={60}
                  inputProps={{
                    onSubmitEditing: handleSendMessage,
                  }}
                />
              </Col>
              <Col marginLeft={8}>
                <AntButton
                  size="large"
                  loading={sending}
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  style={{ height: 60, minWidth: 100, backgroundColor: COLORS.GREEN }}
                >
                  <Text fontSize={16} fontWeight="600" colorWhite>Send</Text>
                </AntButton>
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
    </Col>
  );
};

